/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import {
    AwesomeIcon,
    ChangeFunction,
    ChangeKeyFunction,
    Checkbox,
    Loader,
    preventDefault,
    SelectItemShape
} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import React, {
    FocusEventHandler,
    KeyboardEventHandler,
    MouseEventHandler,
    ReactNode,
    useEffect,
    useRef,
    useState
} from 'react';
import {UserRight, UserRightItem, UserRole} from "../../../../util/interfaces";
import Invitation from "../../../../models/invitationModel";
import AccountUser from "../../../../models/accountUserModel";
import {BolmateButton, BolmateInput, BolmateSelect} from "../../../controls";
import {AVAILABLE_USER_RIGHTS, translateUserRole} from "../../../../util/accessUtil";
import {delay} from 'lodash';

interface SharedProps {
    onSubmit: MouseEventHandler & KeyboardEventHandler;
    onClose: MouseEventHandler;
    onChangeRight: (right: UserRight) => ChangeFunction<boolean>;
    onChangeRightGroup: (rightItem: UserRightItem) => ChangeFunction<boolean>;
    onChangeObject: ChangeKeyFunction;
    onChangeRole: ChangeFunction<UserRole>;
    obj: Invitation | AccountUser;
    loading: boolean;
    active: boolean;
}

export function AccountUserSettings(props: SharedProps) {
    const {obj, loading} = props;

    function renderUserDetails() {
        const name = obj instanceof Invitation ? obj.email : obj.fullName;
        return (
            <div className='user-details'>
                <h2>{name}</h2>
            </div>
        )
    }

    return (
        <form className='account-user' onSubmit={preventDefault}>
            <Loader active={loading}/>
            {renderUserDetails()}
            <RightsSelection {...props} submitText='Opslaan'/>
        </form>
    )
}

interface NewInvitationProps extends SharedProps {
    onEnterEmail: FocusEventHandler;
    obj: Invitation;
    emailAlreadyAdded: boolean;
}

export function NewInvitationForm(props: NewInvitationProps) {
    const {
        obj, onChangeObject, onEnterEmail, loading, emailAlreadyAdded
    } = props;

    function renderEmailConfirmation() {
        if (!emailAlreadyAdded) return null;
        if (emailAlreadyAdded) return (
            <label className='user-exists error'><AwesomeIcon name='times-circle' className='check-icon'/> Deze
                gebruiker heeft al toegang tot het account.</label>
        );
    }

    return (
        <form className='new-invitation' onSubmit={preventDefault}>
            <Loader active={loading}/>
            <div className='email-row'>
                <BolmateInput onChange={onChangeObject('email')} value={obj.email} label='E-mailadres'
                              inputProps={{placeholder: 'E-mailadres', type: 'email'}}
                              onBlur={onEnterEmail} id='invitation-mail'/>
                {renderEmailConfirmation()}
            </div>
            <RightsSelection {...props} submitText={<span>
                Gebruiker toevoegen voor +&euro;5/maand
            </span>} submitDisabled={!obj.email || emailAlreadyAdded}/>
        </form>
    )
}

function findRight(rightItem: UserRightItem, rights: Array<UserRight>): UserRight {
    return rights.find((right: UserRight) => right.key === rightItem.key) ?? {...rightItem, allow: false};
}

const rightsTabs: Array<string> = Object.keys(AVAILABLE_USER_RIGHTS);

interface RightsSelectionProps extends SharedProps {
    submitText: ReactNode;
    submitDisabled?: boolean;
}

const USER_ROLE_SELECT_ITEMS: Array<SelectItemShape> = [
    {label: 'Kies rol...', value: '', selectable: false,},
    {
        label: (
            <div className='user-role-item'>
                <label className='user-role-label'>{translateUserRole(UserRole.CUSTOMER_SERVICE)}</label>
                <div className='user-role-info'>
                    <AwesomeIcon name='info-circle' className='info-icon'/>
                    <p className='info-message' style={{width: '20rem'}}>Heeft geen toegang tot plekken met financiële
                        cijfers (sales dashboard, btw hulptool, exports, etc). Kan enkel factuuracties uitvoeren.</p>
                </div>
            </div>
        ), value: UserRole.CUSTOMER_SERVICE,
        valueLabel: translateUserRole(UserRole.CUSTOMER_SERVICE),
    },
    {
        label: (
            <div className='user-role-item'>
                <label className='user-role-label'>{translateUserRole(UserRole.EMPLOYEE)}</label>
                <div className='user-role-info'>
                    <AwesomeIcon name='info-circle' className='info-icon'/>
                    <p className='info-message' style={{width: '20rem'}}>Kan alles behalve gebruikers
                        toevoegen/verwijderen, abonnement stoppen of verkoopaccount verwijderen</p>
                </div>
            </div>
        ), value: UserRole.EMPLOYEE,
        valueLabel: translateUserRole(UserRole.EMPLOYEE),
    },
    {
        label: (
            <div className='user-role-item'>
                <label className='user-role-label'>{translateUserRole(UserRole.ACCOUNTANT)}</label>
                <div className='user-role-info top'>
                    <AwesomeIcon name='info-circle' className='info-icon'/>
                    <p className='info-message' style={{width: '20rem'}}>Heeft alleen-lezen rechten. Kan wel bekijken,
                        downloaden en exporten, maar kan geen wijzigingen doorvoeren.</p>
                </div>
            </div>
        ), value: UserRole.ACCOUNTANT,
        valueLabel: translateUserRole(UserRole.ACCOUNTANT),
    },
    {
        label: (
            <div className='user-role-item'>
                <label className='user-role-label'>{translateUserRole(UserRole.ADMIN)}</label>
                <div className='user-role-info top'>
                    <AwesomeIcon name='info-circle' className='info-icon'/>
                    <p className='info-message' style={{width: '20rem'}}>Heeft alle rechten, ook gebruikers toevoegen en
                        verwijderen.</p>
                </div>
            </div>
        ), value: UserRole.ADMIN,
        valueLabel: translateUserRole(UserRole.ADMIN),
    },
];

function RightsSelection(props: RightsSelectionProps) {
    const {
        obj, onChangeRight, onChangeRightGroup, onChangeRole, loading, onSubmit, active, submitText,
        submitDisabled
    } = props;
    const [activeTab, setActiveTab] = useState<string>(rightsTabs[0]);
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    function onToggleOpen() {
        setOpen(!open);
    }

    function onChangeActiveTab(tab: string) {
        return function () {
            if (tab !== activeTab)
                setActiveTab(tab);
            containerRef.current?.scrollTo({top: 0, behavior: 'smooth'});
        }
    }

    function renderHeader() {
        return (
            <div className='user-rights-header' onClick={onToggleOpen}>
                <div className='line'/>
                <label className='user-rights-header-label'>Gedetailleerd</label>
                <AwesomeIcon name='chevron-up' className='chev-icon'/>
            </div>
        )
    }

    function renderRole() {
        const role = obj.userRole;
        return (
            <div className='role-container'>
                <BolmateSelect onChange={onChangeRole} value={role ?? ''} items={USER_ROLE_SELECT_ITEMS} label='Rol'
                               highlightSelected/>
            </div>
        )

    }

    function renderTabs() {
        return (
            <div className='tabs-container'>
                {rightsTabs.map(renderTab)}
            </div>
        )
    }

    function renderTab(tab: string, index: number) {
        const isActive = activeTab === tab;
        const allRights = AVAILABLE_USER_RIGHTS[tab];
        const activeRights = allRights.reduce((prev: number, right: UserRightItem) => {
            let next: number = prev;
            if (right.children)
                for (const child of right.children) {
                    if (findRight(child, obj.rights).allow) next++;
                }
            else if (findRight(right, obj.rights).allow) next++;
            return next
        }, 0);
        return (
            <div key={index} className={classNames('tab', isActive && 'active')} onClick={onChangeActiveTab(tab)}>
                <label className='tab-label'>{tab} ({activeRights})</label>
            </div>
        )
    }

    function renderRights() {
        const rights = AVAILABLE_USER_RIGHTS[activeTab];
        const half = Math.ceil(rights.length / 2);
        const leftCol = rights.slice(0, half);
        const rightCol = rights.slice(half);
        return (
            <div className='rights-container'>
                <div className='rights-col'>
                    {leftCol.map(renderRight(false))}
                </div>
                <div className='rights-col'>
                    {rightCol.map(renderRight(false))}
                </div>
            </div>
        )
    }

    function getRightValue(item: UserRightItem, right: UserRight): boolean | 'partial' {
        if (!item.children) return right.allow;
        let hasTrue: boolean = false, hasFalse: boolean = false;
        for (const child of item.children) {
            const _right = findRight(child, obj.rights);
            if (_right.allow) hasTrue = true;
            else hasFalse = true;
            if (hasTrue && hasFalse) return 'partial';
        }
        return hasTrue;
    }

    function renderRight(hasParent: boolean) {
        return function (rightItem: UserRightItem) {
            const userRight = findRight(rightItem, obj.rights);
            const hasChildren = (rightItem.children?.length ?? 0) > 0;
            const groupValue = getRightValue(rightItem, userRight);
            return (
                <div key={rightItem.key}
                     className={classNames('right-container', hasParent && 'has-parent', hasChildren && 'has-children')}>
                    {hasChildren ?
                        <Checkbox checked={groupValue} onCheck={onChangeRightGroup(rightItem)}
                                  label={rightItem.label}/> :
                        <Checkbox checked={userRight.allow} onCheck={onChangeRight(userRight)}
                                  label={rightItem.label}/>
                    }
                    {rightItem.children?.map(renderRight(true))}
                </div>
            )
        }
    }

    function renderFooter() {
        const disabled = loading || submitDisabled || !obj.userRole;
        return (
            <div className='footer-container'>
                <BolmateButton onClick={onSubmit} label={submitText} disabled={disabled}/>
            </div>
        )
    }

    useEffect(() => {
        // Restore first tab after 200ms (closing transition)
        if (!active) {
            delay(setActiveTab, 200, rightsTabs[0]);
            setOpen(false);
        }
    }, [active]);

    return (
        <div className={classNames('user-rights-container', !open && 'closed')}>
            {renderRole()}
            {renderHeader()}
            {renderTabs()}
            {renderRights()}
            {renderFooter()}
        </div>
    )
}